import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Download, Search, X, ExternalLink } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Label } from '@/components/ui/Label';
import { Button } from '@/components/ui/Button';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { exportAuditCsv, useAuditLogs } from '@/api/audit';
import { useOrganizations } from '@/api/organizations';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useToast } from '@/hooks/useToast';
import { apiError } from '@/lib/api';
import type { AuditLog } from '@/types/api';

const ACTION_LABEL: Record<string, string> = {
  'auth.login': 'Signed in',
  'auth.logout': 'Signed out',
  'auth.sso.login': 'Signed in via SSO',
  'auth.mfa.verify': 'Verified 2FA',
  'auth.mfa.setup': 'Set up 2FA',
  'auth.mfa.reset': 'Reset 2FA',
  'auth.password.reset': 'Reset password',
  'file.upload': 'Uploaded file',
  'file.download': 'Downloaded file',
  'file.delete': 'Deleted file',
  'file.restore': 'Restored file',
  'file.rename': 'Renamed file',
  'file.archive': 'Archived file',
  'file.move': 'Moved file',
  'file.version.upload': 'Uploaded new version',
  'file.version.download': 'Downloaded file version',
  'folder.create': 'Created folder',
  'folder.delete': 'Deleted folder',
  'folder.archive': 'Archived folder',
  'folder.rename': 'Renamed folder',
  'folder.move': 'Moved folder',
  'invite.create': 'Sent invite',
  'invite.accept': 'Accepted invite',
  'user.update': 'Updated user',
  'user.disable': 'Disabled user',
  'user.enable': 'Enabled user',
  'user.delete': 'Deleted user',
  'user.password.change': 'Changed password',
  'user.password.reset': 'Reset password',
  'user.mfa.reset': 'Reset MFA',
  'request.create': 'Created request',
  'request.submit': 'Submitted request',
  'request.review': 'Reviewed request',
  'request.cancel': 'Cancelled request',
  'request.delete': 'Deleted request',
  'org.update': 'Updated organization',
};

const FILE_ACTIONS = new Set([
  'file.upload', 'file.download', 'file.delete', 'file.restore', 'file.rename',
  'file.archive', 'file.move', 'file.version.upload', 'file.version.download',
]);

const PAGE_LIMIT = 50;

function targetLabel(log: AuditLog): string | null {
  const m = log.metadata as Record<string, unknown> | null | undefined;
  if (!m) return null;
  if (typeof m.name === 'string') return m.name;
  if (typeof m.to === 'string') return m.to;
  if (typeof m.folder_name === 'string') return m.folder_name;
  return null;
}

function DetailContent({
  selected,
  orgNameById,
}: {
  selected: AuditLog;
  orgNameById: Map<string, string>;
}) {
  const isFileAction = FILE_ACTIONS.has(selected.action) || selected.target_type === 'file';
  const isFolderAction = selected.target_type === 'folder';
  const label = targetLabel(selected);

  return (
    <dl className="space-y-3 text-sm">
      <div>
        <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Action</dt>
        <dd className="text-slate-800">{ACTION_LABEL[selected.action] ?? selected.action}</dd>
        <dd className="text-xs text-slate-400 font-mono mt-0.5">{selected.action}</dd>
      </div>
      <div>
        <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Who</dt>
        <dd className="text-slate-800">{selected.actor_name ?? 'System'}</dd>
        {selected.actor_email && (
          <dd className="text-xs text-slate-400">{selected.actor_email}</dd>
        )}
      </div>
      <div>
        <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">When</dt>
        <dd className="text-slate-800">{selected.created_at ? format(new Date(selected.created_at), 'PPpp') : '—'}</dd>
      </div>
      {selected.organization_id && (
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Organization</dt>
          <dd className="text-slate-800">
            {orgNameById.get(selected.organization_id) ?? (
              <span className="text-xs text-slate-500 font-mono">{selected.organization_id}</span>
            )}
          </dd>
        </div>
      )}
      {selected.source_ip && (
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">IP address</dt>
          <dd className="text-slate-800 font-mono text-xs">{selected.source_ip}</dd>
        </div>
      )}
      {selected.target_id && (
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Target</dt>
          {label && <dd className="text-slate-800 text-sm mb-0.5">{label}</dd>}
          {isFileAction ? (
            <dd>
              <Link
                to={`/files/${selected.target_id}`}
                className="inline-flex items-center gap-1 text-xs text-[#0410BD] hover:underline font-mono"
              >
                {selected.target_id}
                <ExternalLink size={10} />
              </Link>
            </dd>
          ) : isFolderAction && selected.organization_id ? (
            <dd>
              <Link
                to={`/workspace/${selected.organization_id}/folders/${selected.target_id}`}
                className="inline-flex items-center gap-1 text-xs text-[#0410BD] hover:underline font-mono"
              >
                {selected.target_id}
                <ExternalLink size={10} />
              </Link>
            </dd>
          ) : (
            <dd className="text-xs text-slate-500 font-mono break-all">{selected.target_id}</dd>
          )}
          {selected.target_type && (
            <dd className="text-xs text-slate-400 capitalize mt-0.5">{selected.target_type.replace(/_/g, ' ')}</dd>
          )}
        </div>
      )}
      {selected.outcome && (
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Outcome</dt>
          <dd className={`text-sm capitalize ${selected.outcome === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>
            {selected.outcome}
          </dd>
        </div>
      )}
      {selected.metadata && Object.keys(selected.metadata).length > 0 && (
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Details</dt>
          <dd>
            <div className="rounded border border-[#E3E3F1] divide-y divide-[#E3E3F1]">
              {Object.entries(selected.metadata).map(([k, v]) => (
                <div key={k} className="flex items-start gap-2 px-2 py-1.5">
                  <span className="text-xs text-slate-500 w-28 shrink-0 capitalize">{k.replace(/_/g, ' ')}</span>
                  <span className="text-xs text-slate-800 break-all">{String(v)}</span>
                </div>
              ))}
            </div>
          </dd>
        </div>
      )}
    </dl>
  );
}

export function AuditLogPage() {
  usePageTitle('Audit log');
  const orgs = useOrganizations();
  const toast = useToast();
  const [orgId, setOrgId] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState<AuditLog | null>(null);
  const [mobileDetail, setMobileDetail] = useState<AuditLog | null>(null);

  const openEntry = (log: AuditLog) => {
    if (window.innerWidth < 1024) {
      setMobileDetail(log);
    } else {
      setSelected((prev) => (prev?.id === log.id ? null : log));
    }
  };

  const orgNameById = useMemo(
    () => new Map((orgs.data ?? []).map((o) => [o.id, o.name])),
    [orgs.data],
  );

  // Debounce search for server-side filtering
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setOffset(0);
    setSelected(null);
  }, [orgId, debouncedSearch]);

  const { data, isLoading, isError, error } = useAuditLogs({
    org_id: orgId || null,
    actor_q: debouncedSearch || null,
    limit: PAGE_LIMIT,
    offset,
  });

  const allItems = data?.items ?? [];
  const total = data?.total ?? 0;

  // Client-side filter of visible items by action label / target name / IP
  const items = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return allItems;
    return allItems.filter((e) => {
      const actionLabel = (ACTION_LABEL[e.action] ?? e.action).toLowerCase();
      const ip = (e.source_ip || '').toLowerCase();
      const target = (targetLabel(e) || '').toLowerCase();
      const action = (e.action || '').toLowerCase();
      return actionLabel.includes(q) || ip.includes(q) || target.includes(q) || action.includes(q);
    });
  }, [allItems, debouncedSearch]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportAuditCsv({ org_id: orgId || null, action: null, actor_id: null });
      toast.success('Audit CSV downloaded');
    } catch (e) {
      toast.error('Export failed', apiError(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card
        title="Audit log"
        actions={
          <Button variant="secondary" onClick={handleExport} loading={exporting} className="text-xs">
            <Download size={14} /> Export CSV
          </Button>
        }
      >
        <p className="text-xs text-slate-500 mb-3">
          Every authenticated action is logged. Filter by organization and search by user name, email, action, or file name.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="sm:w-56 shrink-0">
            <Label htmlFor="f-org">Organization</Label>
            <Select
              id="f-org"
              value={orgId}
              onChange={(v) => setOrgId(v)}
              options={(orgs.data ?? []).map((o) => ({ value: o.id, label: o.name }))}
              placeholder="All organizations"
            />
          </div>
          <div className="flex-1">
            <Label htmlFor="f-search">Search</Label>
            <div className="flex items-center gap-1.5 border border-[#BABACE] rounded-[6px] px-3 py-2 bg-white focus-within:ring-2 focus-within:ring-bb-navy/30 focus-within:border-bb-navy">
              <Search size={14} className="text-slate-400 shrink-0" />
              <input
                id="f-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="User name, email, action (e.g. file.upload), IP…"
                className="flex-1 min-w-0 text-sm bg-transparent outline-none text-slate-800 placeholder:text-slate-400"
              />
              {search && (
                <button type="button" onClick={() => setSearch('')} className="text-slate-400 hover:text-slate-600">
                  <X size={13} />
                </button>
              )}
            </div>
          </div>
        </div>
      </Card>

      {isLoading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : isError ? (
        <div className="text-sm text-red-600">{apiError(error)}</div>
      ) : allItems.length === 0 ? (
        <EmptyState
          title="No audit entries"
          description="No matching audit entries. Try widening your filters."
          action={
            (orgId || search) ? (
              <Button variant="secondary" onClick={() => { setOrgId(''); setSearch(''); }}>Clear filters</Button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
          {/* Left: log list */}
          <div className="lg:col-span-2">
            <Card padded={false} className="overflow-hidden">
              {items.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-slate-400">
                  No results matching <strong>"{debouncedSearch}"</strong>
                </div>
              ) : (
                <>
                  <div className="overflow-y-auto max-h-[calc(100vh-320px)] custom-scrollbar divide-y divide-slate-100 dark:divide-[#2A2A4A]">
                    {items.map((log) => {
                      const isActive = selected?.id === log.id;
                      const label = targetLabel(log);
                      const orgName = log.organization_id ? orgNameById.get(log.organization_id) : null;
                      return (
                        <div
                          key={log.id}
                          onClick={() => openEntry(log)}
                          className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors ${isActive ? 'bg-[#EFF0FF] dark:bg-[#1A1A3E]' : 'hover:bg-slate-50 dark:hover:bg-white/5'}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <span className={`text-sm font-medium ${isActive ? 'text-[#0410BD]' : 'text-slate-800 dark:text-white'}`}>
                                {log.actor_name || log.actor_email || 'System'}
                              </span>
                              <span className="text-sm text-slate-500 dark:text-[#8888AA]">
                                {ACTION_LABEL[log.action] ?? log.action}
                              </span>
                              {label && (
                                <span className="text-xs text-slate-600 dark:text-[#AAAACC] font-medium truncate max-w-[160px]">
                                  {label}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-400 dark:text-[#8888AA] mt-0.5">
                              {log.created_at ? format(new Date(log.created_at), 'PP p') : '—'}
                              {log.source_ip && ` · ${log.source_ip}`}
                              {orgName && ` · ${orgName}`}
                            </div>
                          </div>
                          {log.outcome && log.outcome !== 'success' && (
                            <span className="text-xs text-red-600 bg-red-50 px-1.5 py-0.5 rounded shrink-0">
                              {log.outcome}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="border-t border-slate-100 dark:border-[#2A2A4A] px-4 py-2">
                    <Pagination
                      total={total}
                      limit={PAGE_LIMIT}
                      offset={offset}
                      onChange={(o) => { setOffset(o); setSelected(null); }}
                    />
                  </div>
                </>
              )}
            </Card>
          </div>

          {/* Right: detail panel (desktop only) */}
          <div className="hidden lg:block lg:sticky lg:top-4">
            {selected ? (
              <Card>
                <div className="flex items-start justify-between mb-4">
                  <span className="text-sm font-semibold text-slate-800">Event detail</span>
                  <button type="button" onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-600">
                    <X size={14} />
                  </button>
                </div>
                <DetailContent selected={selected} orgNameById={orgNameById} />
              </Card>
            ) : (
              <div className="border border-dashed border-slate-200 dark:border-[#2A2A4A] rounded-lg p-6 text-center text-sm text-slate-400">
                Click an entry to see details
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile: detail modal (only fires when screen < lg) */}
      <Modal
        open={!!mobileDetail}
        onClose={() => setMobileDetail(null)}
        title={mobileDetail ? (ACTION_LABEL[mobileDetail.action] ?? mobileDetail.action) : ''}
        size="md"
      >
        {mobileDetail && <DetailContent selected={mobileDetail} orgNameById={orgNameById} />}
      </Modal>
    </div>
  );
}
